import React, { FC, useEffect, useState } from "react";
import * as EmailValidator from "email-validator";
import { useTranslation } from "react-i18next";
import { cpf } from "cpf-cnpj-validator";
import { VbzInputText, VbzDropdown } from "@smiles/voebiz-ui-kit";

import { RegisterContent } from "../../../../components";

import { formatCpf, regexPhoneFormatter } from "../../../../utils/documentUtils";
import { AdminInfoForm, handleAdminFormChangeF } from "../type";

type itemsDescription = {
  id: string;
  description: string;
};
type DropdownsItem = {
  occupationDataData: itemsDescription[];
};
interface AdminInfoProps {
  data: AdminInfoForm;
  onChange: handleAdminFormChangeF;
}
const AdminInfo: FC<AdminInfoProps> = ({ data, onChange }) => {
  const { t } = useTranslation("register_admin");
  const [validatedCPF, setValidatedCPF] = useState<boolean>(false);
  const [validatedEmail, setValidatedEmail] = useState<boolean>(false);
  const [infoFormData, setInfoFormData] = useState<AdminInfoForm>({ ...data });
  const [dropdowns, setDropdowns] = useState<DropdownsItem>({
    occupationDataData: [] as itemsDescription[],
  });
  const handleChangePhone = (e: React.ChangeEvent<HTMLInputElement>, type: "phone1" | "phone2") => {
    e.persist();
    const formatted = regexPhoneFormatter(e);
    const formattedNumber = formatted.replace(/[^0-9]/g, "");
    const areaCode = parseInt(formattedNumber.substring(0, 2), 10);
    const number = formattedNumber.substring(2).trimStart();

    setInfoFormData((prevState) => ({ ...prevState, [type]: { areaCode, number, formatted } }));
  };

  const handleKeyPressNumber = (e: React.KeyboardEvent) => {
    if (!(e.key >= "0" && e.key <= "9") && !(e.key === "-" || e.key === " ")) {
      e.preventDefault();
    }
  };
  const handleKeyPressLetters = (e: React.KeyboardEvent) => {
    if (!e.key.match(/[a-z\sáéíóúàèìòùäëïöüâêîôûãõç]/i)) {
      e.preventDefault();
    }
  };
  const handleAdminNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const input = e.target.value.replace(/[^a-z\sáéíóúàèìòùäëïöüâêîôûãõç]/gi, "");
    setInfoFormData((prevState) => ({ ...prevState, adminName: input }));
  };
  const formatName = (adminName: string) => {
    const cleanAdminName = adminName.trimEnd();
    const firstName = cleanAdminName.split(" ").slice(0, -1).join(" ");
    const lastName = cleanAdminName.split(" ").slice(-1).join(" ");
    setInfoFormData((prevState) => ({ ...prevState, firstName, lastName }));
  };
  const validateCpfFormat = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const validated = cpf.isValid(e.target.value);
    const doc = formatCpf(e.target.value);
    setInfoFormData((prevState) => ({ ...prevState, cpf: { value: doc, validated } }));
    setValidatedCPF(validated);
  };
  const validateEmailFormat = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    if (e.target.value) {
      const validated = EmailValidator.validate(e.target.value);
      setInfoFormData((prevState) => ({ ...prevState, email: { value: e.target.value, validated } }));
      setValidatedEmail(validated);
    }
  };
  const loadListOfValues = async () => {
    const localStorageListOfValuesData = localStorage.getItem("listOfValuesData");
    if (localStorageListOfValuesData) {
      const result = JSON.parse(localStorageListOfValuesData);
      const list = result.find(({ lovType }: { lovType: string }) => lovType === "FINS_DRVOCC_DWLOCC_MLOV");
      setDropdowns({ occupationDataData: list.dadosEmpresa });
    }
  };
  useEffect(() => {
    formatName(infoFormData.adminName);
  }, [infoFormData.adminName]);
  useEffect(() => {
    loadListOfValues();
  }, []);
  useEffect(() => {
    if (infoFormData.cpf.value.length < 3) {
      setValidatedCPF(true);
    }
    if (infoFormData.email.value.length < 3) {
      setValidatedEmail(true);
    }
  }, [infoFormData.cpf.value, infoFormData.email.value]);
  useEffect(() => {
    if (infoFormData.cpf.value.length === 14) {
      setValidatedCPF(cpf.isValid(infoFormData.cpf.value));
      setInfoFormData((prevState) => ({ ...prevState, cpf: { value: prevState.cpf.value, validated: true } }));
    }

    if (infoFormData.email.value.length > 5) {
      setValidatedEmail(EmailValidator.validate(infoFormData.email.value));
      setInfoFormData((prevState) => ({ ...prevState, email: { value: prevState.email.value, validated: true } }));
    }
  }, []);
  useEffect(() => {
    onChange("info", infoFormData);
  }, [infoFormData]);
  return (
    <div className="voebiz-register-cnpj voebiz-register-cnpj-section-1">
      <div className="voebiz-title-wrapper">
        <h3 className="title-wrapper" id="lbl_titleCompany">
          {t("data_title")}
        </h3>
      </div>
      <div className="voebiz-content-wrapper">
        <RegisterContent idMessage="lbl_msgInformativeCompanyData" text={t("data_description")} label="">
          <div>
            <div className="voebiz-company-container-data-fields-right">
              <div className="inputTextContainer">
                <VbzInputText
                  id="input_cpf"
                  placeholder={t("input.text.cpf.placeholder")}
                  maxLength={14}
                  textLabel={t("input.text.cpf.label")}
                  inputMode="numeric"
                  onKeyPress={handleKeyPressNumber}
                  onChange={(e) => {
                    validateCpfFormat(e);
                  }}
                  onFocus={(e) => {
                    validateCpfFormat(e);
                  }}
                  onBlur={(e) => {
                    validateCpfFormat(e);
                  }}
                  value={infoFormData.cpf.value}
                  forceError={!validatedCPF}
                  errorMessage="O CPF informado não é válido. Por favor, tente de novo."
                />
              </div>
              <div className="inputTextContainer">
                <VbzInputText
                  type="text"
                  id="inp_admin_name"
                  placeholder={t("input.text.admin_name.placeholder")}
                  onChange={handleAdminNameChange}
                  onFocus={handleAdminNameChange}
                  onBlur={handleAdminNameChange}
                  value={infoFormData.adminName}
                  textLabel={t("input.text.admin_name.label")}
                  onKeyPress={handleKeyPressLetters}
                />
              </div>
              <div className="inputTextContainer">
                <VbzDropdown
                  id="inp_occupationData"
                  placeholder={t("input.text.occupationData.placeholder")}
                  textLabel={t("input.text.occupationData.label")}
                  errorMessage="Selecione a área de atuação vinculada ao seu cargo."
                  onClick={(value) => {
                    const result = dropdowns.occupationDataData.find((item) => item.description === value);
                    if (result && result.id && result.description) {
                      setInfoFormData((prevState) => ({
                        ...prevState,
                        occupationDataData: {
                          id: result.id,
                          description: result.description,
                        },
                      }));
                    } else {
                      setInfoFormData(data);
                    }
                  }}
                  actions={dropdowns.occupationDataData.map((item) => ({ name: item.description }))}
                  defaultValue={data.occupationDataData.description}
                />
              </div>
              <div className="inputTextContainer">
                <VbzInputText
                  id="inp_email"
                  placeholder={t("input.text.email.placeholder")}
                  textLabel={t("input.text.email.label")}
                  errorMessage="O e-mail informado não é válido. Por favor, tente de novo."
                  inputMode="text"
                  onChange={(e) => {
                    validateEmailFormat(e);
                  }}
                  onFocus={(e) => {
                    validateEmailFormat(e);
                  }}
                  onBlur={(e) => {
                    validateEmailFormat(e);
                  }}
                  value={infoFormData.email.value}
                  forceError={!validatedEmail}
                />
              </div>
              <div className="inputTextContainer">
                <VbzInputText
                  id="inp_phone_number_1"
                  placeholder={t("input.text.phone_number_1.placeholder")}
                  maxLength={15}
                  textLabel={t("input.text.phone_number_1.label")}
                  inputMode="tel"
                  onChange={(e) => handleChangePhone(e, "phone1")}
                  onFocus={(e) => handleChangePhone(e, "phone1")}
                  onBlur={(e) => handleChangePhone(e, "phone1")}
                  value={infoFormData.phone1.formatted}
                  onKeyPress={handleKeyPressNumber}
                />
              </div>
              <div className="inputTextContainer">
                <VbzInputText
                  id="inp_phone_number_2"
                  placeholder={t("input.text.phone_number_2.placeholder")}
                  onChange={(e) => handleChangePhone(e, "phone2")}
                  value={infoFormData.phone2.formatted}
                  maxLength={15}
                  inputMode="tel"
                  onKeyPress={handleKeyPressNumber}
                  textLabel={t("input.text.phone_number_2.label")}
                />
              </div>
            </div>
          </div>
        </RegisterContent>
      </div>
    </div>
  );
};

export default AdminInfo;
